import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Locale = 'ru' | 'en';

type Dict = Record<string, string>;

const RU: Dict = {
  // brand
  'brand.name': 'ExecOS',
  'brand.tagline': 'Операционная система для людей',
  'brand.footer': 'На базе YandexGPT.\nВсе AI-вызовы на сервере.',

  // nav
  'nav.dashboard': 'Сегодня',
  'nav.inbox': 'Входящие',
  'nav.daily': 'План дня',
  'nav.decision': 'Решить',

  // dashboard
  'dashboard.title': 'Сегодня',
  'dashboard.generate': 'Собрать AI-план',
  'dashboard.generating': 'Думаю…',
  'dashboard.section.capture': 'Захват',
  'dashboard.section.recent': 'Недавнее',
  'dashboard.section.suggestions': 'AI-подсказки',
  'dashboard.empty.capture': 'Запиши что-нибудь, чтобы начать.',
  'dashboard.empty.noplan': 'Нажми «Собрать AI-план», чтобы получить следующие действия на основе записей.',
  'dashboard.thinking': 'Анализ твоих записей в YandexGPT-pro…',

  // inbox
  'inbox.title': 'Входящие',
  'inbox.subtitle': 'Память. Всё, что ты фиксируешь, живёт здесь.',
  'inbox.section.capture': 'Захват',
  'inbox.section.memory': 'Память',
  'inbox.filter.all': 'Все',
  'inbox.filter.task': 'Задачи',
  'inbox.filter.note': 'Заметки',
  'inbox.filter.idea': 'Идеи',
  'inbox.loading': 'Загрузка…',
  'inbox.empty': 'Пока пусто.',

  // daily
  'daily.title': 'План дня',
  'daily.subtitle': 'Собери всё в один реалистичный план на день.',
  'daily.focus.placeholder': 'Фокус на сегодня (опционально) — например, «выпустить v1 Входящих»',
  'daily.generate': 'Сформировать день',
  'daily.generating': 'Планирую…',
  'daily.section.priorities': 'Главные приоритеты',
  'daily.section.schedule': 'Расписание',
  'daily.empty.schedule': 'Расписание не получено.',

  // decision
  'decision.title': 'Решить',
  'decision.subtitle': 'Опиши решение — получи плюсы, минусы, риски и рекомендацию.',
  'decision.label.decision': 'Решение',
  'decision.placeholder.decision': 'Например: Поднимать ли seed-раунд сейчас или 6 месяцев бутстрэпа?',
  'decision.label.context': 'Контекст (опционально)',
  'decision.placeholder.context': 'Фон, ограничения, ставки…',
  'decision.label.options': 'Варианты (опционально, по одному на строке)',
  'decision.placeholder.options': 'Поднять сейчас\nБутстрэпить ещё 6 месяцев\nПоднять меньший ангельский',
  'decision.analyze': 'Проанализировать через YandexGPT',
  'decision.analyzing': 'Анализирую…',
  'decision.result': 'Результат',
  'decision.confidence': 'уверенность',
  'decision.section.pros': 'Плюсы',
  'decision.section.cons': 'Минусы',
  'decision.section.risks': 'Риски',
  'decision.section.recommended': 'Рекомендуемое действие',

  // quick capture
  'capture.placeholder': 'Запиши что угодно — задачу, заметку, идею…',
  'capture.placeholder.long': 'Что у тебя на уме?',
  'capture.kind.task': 'Задача',
  'capture.kind.note': 'Заметка',
  'capture.kind.idea': 'Идея',
  'capture.tags.placeholder': 'теги (через запятую)',
  'capture.button': 'Добавить',
  'capture.saving': 'Сохраняю…',

  // item list
  'item.justnow': 'только что',
  'item.m.ago': 'м назад',
  'item.h.ago': 'ч назад',
  'item.d.ago': 'д назад',

  // common
  'confidence.low': 'низкая',
  'confidence.medium': 'средняя',
  'confidence.high': 'высокая',
  'priority.low': 'низкий',
  'priority.medium': 'средний',
  'priority.high': 'высокий',
  'lang.toggle.label': 'Язык',

  // landing
  'landing.badge': 'На базе YandexGPT',
  'landing.hero.title': 'ОС для твоей головы.\nAI, который реально исполняет.',
  'landing.hero.sub': 'ExecOS — личный исполнительный ассистент: фиксирует задачи, помнит контекст, строит план дня и помогает принимать решения. Всё на YandexGPT, твои данные не уходят за пределы Яндекс.Облака.',
  'landing.cta.primary': 'Войти через Яндекс',
  'landing.cta.demo': 'Попробовать демо',
  'landing.cta.note': 'Бесплатно на старте • без карты • ваши данные приватны',
  'landing.features.title': 'Как это работает',
  'landing.feat.capture.title': 'Захват за 2 секунды',
  'landing.feat.capture.body': 'Одним полем — задача, заметка или идея. Теги, память, никогда не теряется.',
  'landing.feat.plan.title': 'AI-план, а не список',
  'landing.feat.plan.body': 'YandexGPT-pro выбирает важное и объясняет, почему это важно сейчас.',
  'landing.feat.decide.title': 'Решения с аргументами',
  'landing.feat.decide.body': 'Плюсы, минусы, риски, рекомендация. Без воды, за секунды.',
  'landing.feat.daily.title': 'Реалистичный день',
  'landing.feat.daily.body': 'Расписание по часам на основе твоих задач и фокуса.',
  'landing.pricing.title': 'Тарифы',
  'landing.pricing.note': 'Бета: все функции бесплатно. Платные тарифы — после релиза.',
  'landing.plan.free.title': 'Free',
  'landing.plan.free.price': '0 ₽',
  'landing.plan.free.period': 'навсегда',
  'landing.plan.free.f1': 'Захват задач и заметок',
  'landing.plan.free.f2': '20 AI-вызовов в месяц',
  'landing.plan.free.f3': 'RU + EN интерфейс',
  'landing.plan.pro.title': 'Pro',
  'landing.plan.pro.price': '790 ₽',
  'landing.plan.pro.period': 'в месяц',
  'landing.plan.pro.badge': 'Скоро',
  'landing.plan.pro.f1': 'Неограниченно AI-вызовов',
  'landing.plan.pro.f2': 'YandexGPT-pro для глубокого анализа',
  'landing.plan.pro.f3': 'Приоритетная обработка',
  'landing.plan.team.title': 'Team',
  'landing.plan.team.price': 'от 2 490 ₽',
  'landing.plan.team.period': 'в месяц за команду',
  'landing.plan.team.badge': 'Скоро',
  'landing.plan.team.f1': 'Общая память команды',
  'landing.plan.team.f2': 'Роли и доступы',
  'landing.plan.team.f3': 'SLA и поддержка',
  'landing.footer.privacy': 'Все AI-вызовы идут на сервер; ключи YandexGPT не попадают в браузер.',
  'landing.footer.region': 'Хостинг: Яндекс.Облако (ru-central1).',

  // user menu / account
  'auth.signin': 'Войти через Яндекс',
  'auth.signout': 'Выйти',
  'auth.demoBanner': 'Демо-режим — войди через Яндекс, чтобы сохранять данные между устройствами.',
  'auth.demoBanner.cta': 'Войти',
  'auth.menu.account': 'Аккаунт',
  'auth.menu.anon': 'Гость',
  'auth.loading': 'Загрузка…',
  'auth.error.notConfigured': 'Яндекс-вход пока не настроен. Попробуй демо-режим.',
};

const EN: Dict = {
  'brand.name': 'ExecOS',
  'brand.tagline': 'Operating System for Humans',
  'brand.footer': 'Powered by YandexGPT.\nAll AI runs server-side.',

  'nav.dashboard': 'Today',
  'nav.inbox': 'Inbox',
  'nav.daily': 'Daily Plan',
  'nav.decision': 'Decide',

  'dashboard.title': 'Today',
  'dashboard.generate': 'Generate AI plan',
  'dashboard.generating': 'Thinking…',
  'dashboard.section.capture': 'Capture',
  'dashboard.section.recent': 'Recent',
  'dashboard.section.suggestions': 'AI Suggestions',
  'dashboard.empty.capture': 'Capture something to get started.',
  'dashboard.empty.noplan': 'Click Generate AI plan to get your next actions based on everything you\'ve captured.',
  'dashboard.thinking': 'Reasoning over your items with YandexGPT-pro…',

  'inbox.title': 'Inbox',
  'inbox.subtitle': 'Memory. Everything you capture lives here.',
  'inbox.section.capture': 'Capture',
  'inbox.section.memory': 'Memory',
  'inbox.filter.all': 'All',
  'inbox.filter.task': 'Tasks',
  'inbox.filter.note': 'Notes',
  'inbox.filter.idea': 'Ideas',
  'inbox.loading': 'Loading…',
  'inbox.empty': 'Nothing here yet.',

  'daily.title': 'Daily Plan',
  'daily.subtitle': 'Synthesize everything into a single, realistic execution plan.',
  'daily.focus.placeholder': "Focus for today (optional) — e.g. 'ship v1 of Inbox'",
  'daily.generate': 'Generate day',
  'daily.generating': 'Planning…',
  'daily.section.priorities': 'Top priorities',
  'daily.section.schedule': 'Schedule',
  'daily.empty.schedule': 'No schedule returned.',

  'landing.badge': 'Powered by YandexGPT',
  'landing.hero.title': 'An OS for your head.\nAI that actually executes.',
  'landing.hero.sub': 'ExecOS is a personal executive assistant: it captures intent, remembers context, builds your day, and helps you decide. All on YandexGPT. Your data stays in Yandex Cloud.',
  'landing.cta.primary': 'Sign in with Yandex',
  'landing.cta.demo': 'Try the demo',
  'landing.cta.note': 'Free during beta · no card · private by default',
  'landing.features.title': 'How it works',
  'landing.feat.capture.title': 'Capture in 2 seconds',
  'landing.feat.capture.body': 'One field — task, note or idea. Tags, memory, never lost.',
  'landing.feat.plan.title': 'AI plan, not a list',
  'landing.feat.plan.body': 'YandexGPT-pro picks what matters and explains why, now.',
  'landing.feat.decide.title': 'Decisions with reasons',
  'landing.feat.decide.body': 'Pros, cons, risks, recommendation. Seconds, not essays.',
  'landing.feat.daily.title': 'Realistic day',
  'landing.feat.daily.body': 'Hour-by-hour schedule built from your intents and focus.',
  'landing.pricing.title': 'Pricing',
  'landing.pricing.note': 'Beta: everything free. Paid tiers after launch.',
  'landing.plan.free.title': 'Free',
  'landing.plan.free.price': '$0',
  'landing.plan.free.period': 'forever',
  'landing.plan.free.f1': 'Capture tasks & notes',
  'landing.plan.free.f2': '20 AI calls / month',
  'landing.plan.free.f3': 'RU + EN interface',
  'landing.plan.pro.title': 'Pro',
  'landing.plan.pro.price': '$9',
  'landing.plan.pro.period': '/ month',
  'landing.plan.pro.badge': 'Soon',
  'landing.plan.pro.f1': 'Unlimited AI calls',
  'landing.plan.pro.f2': 'YandexGPT-pro reasoning',
  'landing.plan.pro.f3': 'Priority processing',
  'landing.plan.team.title': 'Team',
  'landing.plan.team.price': 'from $29',
  'landing.plan.team.period': '/ month / team',
  'landing.plan.team.badge': 'Soon',
  'landing.plan.team.f1': 'Shared team memory',
  'landing.plan.team.f2': 'Roles & access',
  'landing.plan.team.f3': 'SLA & support',
  'landing.footer.privacy': 'All AI runs server-side; YandexGPT keys never reach the browser.',
  'landing.footer.region': 'Hosted in Yandex Cloud (ru-central1).',

  'auth.signin': 'Sign in with Yandex',
  'auth.signout': 'Sign out',
  'auth.demoBanner': 'Demo mode — sign in with Yandex to keep your data across devices.',
  'auth.demoBanner.cta': 'Sign in',
  'auth.menu.account': 'Account',
  'auth.menu.anon': 'Guest',
  'auth.loading': 'Loading…',
  'auth.error.notConfigured': 'Yandex sign-in is not configured yet. Try the demo.',

  'decision.title': 'Decide',
  'decision.subtitle': 'Describe the decision — get pros, cons, risks, and a recommendation.',
  'decision.label.decision': 'Decision',
  'decision.placeholder.decision': 'e.g. Should I raise a seed round now or bootstrap for 6 more months?',
  'decision.label.context': 'Context (optional)',
  'decision.placeholder.context': 'Relevant background, constraints, stakes…',
  'decision.label.options': 'Options (optional, one per line)',
  'decision.placeholder.options': 'Raise now\nBootstrap 6 more months\nRaise a smaller angel round',
  'decision.analyze': 'Analyze with YandexGPT',
  'decision.analyzing': 'Analyzing…',
  'decision.result': 'Result',
  'decision.confidence': 'confidence',
  'decision.section.pros': 'Pros',
  'decision.section.cons': 'Cons',
  'decision.section.risks': 'Risks',
  'decision.section.recommended': 'Recommended action',

  'capture.placeholder': 'Capture anything — task, note, idea…',
  'capture.placeholder.long': "What's on your mind?",
  'capture.kind.task': 'Task',
  'capture.kind.note': 'Note',
  'capture.kind.idea': 'Idea',
  'capture.tags.placeholder': 'tags (comma-separated)',
  'capture.button': 'Capture',
  'capture.saving': 'Saving…',

  'item.justnow': 'just now',
  'item.m.ago': 'm ago',
  'item.h.ago': 'h ago',
  'item.d.ago': 'd ago',

  'confidence.low': 'low',
  'confidence.medium': 'medium',
  'confidence.high': 'high',
  'priority.low': 'low',
  'priority.medium': 'medium',
  'priority.high': 'high',
  'lang.toggle.label': 'Language',
};

const DICTS: Record<Locale, Dict> = { ru: RU, en: EN };

interface I18nContextValue {
  locale: Locale;
  t: (key: string) => string;
  setLocale: (l: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function detectInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem('execos.locale');
    if (stored === 'ru' || stored === 'en') return stored;
  } catch {
    /* noop */
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : 'ru';
  return browser.startsWith('ru') ? 'ru' : 'ru'; // default RU — product is Russia-first
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem('execos.locale', l);
    } catch {
      /* noop */
    }
    if (typeof document !== 'undefined') document.documentElement.lang = l;
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: string) => {
      const dict = DICTS[locale];
      return dict[key] ?? DICTS.en[key] ?? key;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, t, setLocale }), [locale, t, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function formatAgo(iso: string, t: (k: string) => string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('item.justnow');
  if (mins < 60) return `${mins}${t('item.m.ago')}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}${t('item.h.ago')}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}${t('item.d.ago')}`;
  return d.toLocaleDateString();
}
